import type { TextInputProps } from '@vtex/admin-ui'
import { IconClock, TextInput, get } from '@vtex/admin-ui'
import { useWatch } from '@vtex/admin-ui-form'
import React, { useMemo } from 'react'

import { useFormStateContext } from '../../../providers'

import { toTimeISOString } from './converters'
import './datetimeinput.css'

type Props = {
  label: string
  name: string
  allowMinutes?: boolean
} & TextInputProps

export function TimePicker(props: Props) {
  const { label, name, allowMinutes = true, ...rest } = props
  const { form, readOnly } = useFormStateContext()

  const value: Date | undefined = useWatch({ form, name })

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputTime = event.target.valueAsDate

    if (!inputTime) {
      form.setValue(name, undefined)

      return
    }

    const newValue = value ? new Date(value) : new Date()

    newValue.setHours(inputTime.getUTCHours())

    if (allowMinutes) {
      newValue.setMinutes(inputTime.getUTCMinutes())
    } else {
      newValue.setMinutes(0)
    }

    newValue.setUTCSeconds(0, 0)

    form.setValue(name, newValue)
  }

  const error = get(form.formState.errors, name)

  const formattedTime = useMemo(() => {
    if (!value) {
      return ''
    }
    return toTimeISOString(value)
  }, [value])

  return (
    <TextInput
      label={label}
      type="time"
      disabled={readOnly}
      value={formattedTime}
      onChange={handleChange}
      prefix={<IconClock />}
      step={allowMinutes ? undefined : '3600'}
      {...rest}
      error={!!error?.message}
      errorText={error?.message}
    />
  )
}
